import moment from 'moment'
import classNames from 'classnames'
import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import Formsy from 'formsy-react'

import { extendingDateTimeSelector } from 'extending/selectors'
import { startOfWeekSelector } from 'settings/selectors'

import FormsyInput from 'shared/components/Textfield'
import DatePicker from 'shared/components/TimePicker/DatePicker'
import { DATE_FORMAT } from 'shared/constants'
import featureToggles from 'shared/featureToggles'

import * as constants from '../../constants'
import SurveyModalContent from './SurveyModalContent'
import ConfirmModal from './ConfirmModal'

import {
    getActualSurvey,
    getAllSurveyAdmin,
    createNewSurveyError,
    createNewSurveyResult,
    createNewSurveyPendingSelector,
} from '../../selectors'
import {
    getAllSurveysByCountryAdmin,
    addNewSurvey,
    clearAddSurveySendingResult,
    clearAddSurveySendingError,
} from '../../actions'
import { getIsDateInRange } from '../../utils'

import { surveyTabsCa, surveyTabsBr } from '../routes'

import styles from '../styles.module.scss'

const NewSurveyView = ({
    startOfWeek,
    getAllSurveysByCountryAdmin,
    allSurvey,
    addNewSurvey,
    createNewSurveyError,
    createNewSurveyResult,
    createSurveyPending,
    clearAddSurveySendingResult,
    clearAddSurveySendingError,
}) => {
    const [submitData, setSubmitData] = useState({})
    const [isNextBtnEnabled, setNextBtnEnabled] = useState(false)
    const [showErrorMessage, setShowErrorMessage] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const [, setIsErrorVisible] = useState(false)
    const [requiredFields, setRequiredFields] = useState([])
    const [isToggled, setIsToggled] = useState(false)
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const formRef = useRef()
    const isDateInRange =
        allSurvey && submitData.dateEnd && getIsDateInRange(allSurvey, submitData?.startDate, submitData?.dateEnd)
    const currentCountrySurveyTabs = featureToggles.brNeoPurchaseFlow ? surveyTabsBr : surveyTabsCa
    const firstTabsElementIndex = 0
    const lastTabsElementIndex = currentCountrySurveyTabs.length - 1

    const inputFields = () => [...document.querySelectorAll('input[required], textarea[required]')]
    const isCurrentStepValid = () => requiredFields.every(input => input.value !== '' && !input.validity.valueMissing)

    const validateSubmitData = () => {
        if (activeTab === firstTabsElementIndex && isCurrentStepValid()) {
            setNextBtnEnabled(true)
        } else if (
            activeTab === lastTabsElementIndex &&
            !isToggled &&
            Object.keys(submitData).length === constants.SURVEY_FORM_FIELDS_AMOUNT_MIN &&
            isCurrentStepValid()
        ) {
            setNextBtnEnabled(true)
        } else if (
            activeTab === lastTabsElementIndex &&
            isToggled &&
            Object.keys(submitData).length === constants.SURVEY_FORM_FIELDS_AMOUNT_MAX &&
            isCurrentStepValid()
        ) {
            setNextBtnEnabled(true)
        } else setNextBtnEnabled(false)
    }

    useEffect(() => {
        getAllSurveysByCountryAdmin()
        setRequiredFields(inputFields())
        updateSubmitData([{ name: 'startDate', value: moment(new Date()) }])
    }, [])

    useEffect(() => {
        if (!submitData.startDate) {
            updateSubmitData([{ name: 'startDate', value: moment(new Date()) }])
        }
        submitData.startDate && updateSubmitData([{ name: 'dateEnd', value: submitData.startDate }])
    }, [submitData.startDate])

    useEffect(() => {
        validateSubmitData()
    }, [submitData])

    useEffect(() => {
        validateSubmitData()
    }, [activeTab])

    useEffect(() => {
        if (requiredFields.length > 0) {
            validateSubmitData()
        }
    }, [requiredFields])

    useEffect(() => {
        setRequiredFields(inputFields())
    }, [isToggled])

    useEffect(() => {
        if (createNewSurveyError) {
            setTimeout(() => {
                clearAddSurveySendingError()
            }, 6000)
        }
    }, [createNewSurveyError])

    useEffect(() => {
        if (createNewSurveyResult) {
            setSubmitData({})
            setTimeout(() => {
                clearAddSurveySendingResult()
            }, 6000)
            setActiveTab(firstTabsElementIndex)
        }
    }, [createNewSurveyResult])

    const updateSubmitData = fields => {
        setSubmitData(prevData => {
            const newData = { ...prevData }

            fields.forEach(field => {
                if (field.value === '') {
                    delete newData[field.name]
                } else {
                    newData[field.name] = field.value
                }
            })

            return newData
        })
    }

    const onChangeHandlerTextarea = (e, prefix) => {
        updateSubmitData([{ name: `${prefix}thankYouBody`, value: e }])
    }

    const onChangeHandler = e => {
        updateSubmitData([{ name: e.target.name, value: e.target.value }])
    }

    const onSubmitBtnPress = () => {
        if (activeTab === firstTabsElementIndex) {
            setActiveTab(firstTabsElementIndex + 1)
        } else {
            setShowConfirmationModal(true)
        }
    }

    const onSubmitHandler = event => {
        setShowErrorMessage(false)

        if (isDateInRange) {
            event.preventDefault()
            setShowErrorMessage(true)
        }
        addNewSurvey(submitData)
        onCloseConfirmModal()
    }

    const removeToggleFields = prefix => {
        updateSubmitData([
            { name: 'textResponseRequired', value: '' },
            { name: `${prefix}questionText`, value: '' },
            { name: `${prefix}questionHint`, value: '' },
        ])
    }

    const onToggle = prefix => {
        setIsToggled(prev => !prev)
        !isToggled
            ? updateSubmitData([{ name: 'textResponseRequired', value: !isToggled }])
            : removeToggleFields(prefix)
    }

    const onCloseConfirmModal = () => setShowConfirmationModal(false)

    return (
        <div ref={formRef}>
            {showConfirmationModal && (
                <ConfirmModal
                    onClose={onCloseConfirmModal}
                    onCancel={onCloseConfirmModal}
                    onConfirm={onSubmitHandler}
                />
            )}
            <Formsy>
                <div className={classNames('container', styles.npsContent)}>
                    <div className={styles.addSurveyDateSection}>
                        <h1 className={styles.npsContainerTitle}>
                            <T>nps.admin.create.new.survey.title</T>
                        </h1>
                        <p className="text-large">
                            <T>nps.admin.create.new.survey.input.label</T>
                        </p>
                        <p className="text-base">
                            <T>nps.admin.create.new.survey.input.label.desc</T>
                        </p>
                        <FormsyInput
                            required
                            name="surveyName"
                            onChange={onChangeHandler}
                            value={submitData['surveyName'] || ''}
                            setShowError={setIsErrorVisible}
                        />
                        <div className="d-md-flex d-lg-flex flex-row justify-content-between">
                            <DatePicker
                                label={'Survey start date '}
                                name="startDate"
                                className={classNames('d-flex align-items-center', styles.datePicker)}
                                value={submitData.startDate ? moment(submitData.startDate) : moment(new Date())}
                                displayFormat={DATE_FORMAT}
                                startOfWeek={startOfWeek}
                                required
                                onChange={e => updateSubmitData([{ name: 'startDate', value: e }])}
                            />
                            <DatePicker
                                name="dateEnd"
                                label={'Survey end date '}
                                className={classNames('d-flex align-items-center', styles.datePicker)}
                                value={submitData.dateEnd ? moment(submitData.dateEnd) : moment(new Date())}
                                displayFormat={DATE_FORMAT}
                                isOutsideRange={day => day.isBefore(submitData.startDate)}
                                required
                                onChange={e => updateSubmitData([{ name: 'dateEnd', value: e }])}
                            />
                        </div>
                    </div>
                </div>
                <SurveyModalContent
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isNextBtnEnabled={isNextBtnEnabled}
                    onChange={onChangeHandler}
                    formData={submitData}
                    setShowError={setIsErrorVisible}
                    createSurveyDateError={showErrorMessage}
                    onToggle={onToggle}
                    isToggled={isToggled}
                    onChangeHandlerTextarea={onChangeHandlerTextarea}
                    createSurveyPending={createSurveyPending}
                    createNewSurveyError={createNewSurveyError}
                    createNewSurveyResult={createNewSurveyResult}
                    onSubmitHandler={onSubmitBtnPress}
                    currentCountrySurveyTabs={currentCountrySurveyTabs}
                />
            </Formsy>
        </div>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            range: extendingDateTimeSelector,
            startOfWeek: startOfWeekSelector,
            actualSurvey: getActualSurvey,
            allSurvey: getAllSurveyAdmin,
            createNewSurveyError: createNewSurveyError,
            createNewSurveyResult: createNewSurveyResult,
            createSurveyPending: createNewSurveyPendingSelector,
        }),
        { getAllSurveysByCountryAdmin, addNewSurvey, clearAddSurveySendingResult, clearAddSurveySendingError }
    )
)(NewSurveyView)
